// BubblePublicPage.js - Enhanced with Casting Call Support
// ✅ FIXED: Now properly displays casting calls in feed preview using FeedItem component

import React, { useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "../API";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  Typography, Box, Container, Chip, Stack, Button, Avatar,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  useMediaQuery, Divider, Card, CardContent, Grid, Paper,
  IconButton, Badge, Skeleton, Menu, MenuItem, ListItemIcon, ListItemText,
  Snackbar, Alert
} from "@mui/material";
import { styled, alpha, useTheme } from '@mui/material/styles';
import {
  Public as PublicIcon, Lock as LockIcon, AttachMoney as MoneyIcon,
  CheckCircle as CheckCircleIcon, People as PeopleIcon,
  TrendingUp as TrendingIcon, Chat as ChatIcon,
  Description as DescriptionIcon, Schedule as ScheduleIcon,
  Share as ShareIcon, Image as ImageIcon, VideoLibrary as VideoIcon,
  InsertDriveFile as FileIcon, Folder as FolderIcon,
  Visibility as VisibilityIcon,
  Link as LinkIcon,
  OpenInNew as OpenInNewIcon,
  Campaign as CampaignIcon,
  ArrowForward as ArrowIcon,
} from "@mui/icons-material";
import useAuth from "../Hooks/use-auth";
import { useCreatorAuth } from "../Hooks/creator-use-auth";
import HomeHeader from "../Components/HomeHeader";
import PageTitle from "../Components/PageTitle";
import routes from "../Config/routes";
// ✅ NEW: Import FeedItem for unified post/casting call rendering
import FeedItem from "../CreatorUser/CreatorPages/Dashboard/zapfeed/components/unitedfeed";
// ✅ NEW: Import CastingCallDialog for viewing casting calls
import CastingCallDialog from "../Pages/Dashboard/Campaigns/castingcalls/castingcalldialog";

// ... (keep all existing styled components: ModernGradientHeader, CoverImageBackground, etc.)
const ModernGradientHeader = styled(Box)(({ theme, themeColors, hasCoverImage }) => {
  const primaryColor = themeColors?.primary || theme.palette.primary.main;
  const secondaryColor = themeColors?.secondary || theme.palette.secondary.main;
  
  return {
    background: hasCoverImage 
      ? `linear-gradient(135deg, ${alpha(primaryColor, 0.9)} 0%, ${alpha(secondaryColor, 0.9)} 100%)`
      : `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
    position: "relative",
    overflow: "hidden",
    paddingTop: 80,
    minHeight: 400,
    [theme.breakpoints.down('md')]: {
      paddingTop: 70,
      minHeight: 350,
    },
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)",
      pointerEvents: "none",
      zIndex: 2,
    },
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      height: "40%",
      background: "linear-gradient(to top, rgba(0,0,0,0.3) 0%, transparent 100%)",
      pointerEvents: "none",
      zIndex: 2,
    },
  };
});

const CoverImageBackground = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1,
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
});

const ModernButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1.5, 4),
  fontWeight: 600,
  textTransform: "none",
  fontSize: "1rem",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  boxShadow: theme.shadows[2],
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: theme.shadows[8],
  },
  "&:active": {
    transform: "translateY(0)",
  },
}));

const InfoCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 16,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  transition: "all 0.3s ease",
  border: `1px solid ${theme.palette.divider}`,
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[8],
  },
}));

const StatusChip = styled(Chip)(({ theme }) => ({
  borderRadius: 8,
  fontWeight: 600,
  fontSize: "0.875rem",
  height: 32,
  boxShadow: theme.shadows[1],
}));

const AssetCard = styled(Paper)(({ theme }) => ({
  borderRadius: 12,
  border: `1px solid ${theme.palette.divider}`,
  transition: "all 0.3s ease",
  cursor: "pointer",
  overflow: "hidden",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[8],
    borderColor: theme.palette.primary.main,
  },
}));

const AssetPreview = styled(Box)(({ theme }) => ({
  width: "100%",
  height: 160,
  position: "relative",
  overflow: "hidden",
  backgroundColor: alpha(theme.palette.primary.main, 0.05),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
}));

// ✅ NEW: Styled components for casting call cards
const CastingCallCard = styled(Paper)(({ theme }) => ({
  borderRadius: 12,
  overflow: 'hidden',
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: `2px solid ${alpha(theme.palette.warning.main, 0.3)}`,
  background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.03)} 0%, ${alpha(theme.palette.info.main, 0.03)} 100%)`,
  position: 'relative',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
    borderColor: theme.palette.warning.main,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: `linear-gradient(90deg, ${theme.palette.warning.main}, ${theme.palette.info.main})`,
  },
}));

const CastingCallHeader = styled(Box)(({ theme, headerImage }) => ({
  height: 120,
  background: headerImage 
    ? `url(${headerImage}) center/cover`
    : `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.8)} 0%, ${alpha(theme.palette.info.main, 0.8)} 100%)`,
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.3) 100%)',
  },
}));

const RegularPostCard = styled(Paper)(({ theme }) => ({
  borderRadius: 12,
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.2s ease',
  '&:hover': {
    boxShadow: theme.shadows[2],
  },
}));

export default function BubblePublicPage() {
  const { bubbleId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const qc = useQueryClient();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { isAuthorized: userOK } = useAuth();
  const { isAuthorized: creatorOK, creatorToken } = useCreatorAuth();
  const isAuthenticated = userOK || creatorOK;
  const isCreator = !!creatorToken?.creator_user;

  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [snack, setSnack] = useState(null);

  // ✅ NEW: Casting call view dialog state
  const [castingCallViewDialogOpen, setCastingCallViewDialogOpen] = useState(false);
  const [selectedCastingCall, setSelectedCastingCall] = useState(null);
  const [castingCallPosterInfo, setCastingCallPosterInfo] = useState(null);

  // ✅ NEW: Share menu state for casting calls
  const [shareMenuAnchor, setShareMenuAnchor] = useState(null);
  const [shareMenuActivity, setShareMenuActivity] = useState(null);

  // Fetch bubble data
  const {
    data: bubbleResponse,
    isLoading,
    refetch,
  } = useQuery(
    ["bubble-public", bubbleId],
    async () => {
      if (isAuthenticated) {
        try {
          const response = await API.bubbles.get(bubbleId);
          return response.bubble || response;
        } catch (error) {
          console.log("Falling back to public endpoint");
          return await API.bubbles.getPublic(bubbleId);
        }
      }
      return await API.bubbles.getPublic(bubbleId);
    },
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
    }
  );

  const bubble = bubbleResponse;

  // ✅ UPDATED: Fetch bubble feed preview (now includes casting calls)
  const { data: feedData } = useQuery(
    ["bubble-feed-preview", bubbleId],
    async () => {
      if (isAuthenticated && bubble?.is_member) {
        try {
          return await API.bubbles.getFeed(bubbleId, { page: 0, limit: 3 });
        } catch (error) {
          console.log("Falling back to public feed endpoint");
          return await API.bubbles.getFeedPublic(bubbleId, { page: 0, limit: 3 });
        }
      }
      return await API.bubbles.getFeedPublic(bubbleId, { page: 0, limit: 3 });
    },
    {
      enabled: !!bubble,
      refetchOnWindowFocus: false,
      retry: 1,
    }
  );

  // Fetch bubble assets
  const { data: assetsData, isLoading: assetsLoading } = useQuery(
    ["bubble-assets-public", bubbleId],
    () => API.bubbles.getAssetsPublic(bubbleId),
    {
      enabled: !!bubble,
      refetchOnWindowFocus: false,
    }
  );

  // Join mutation
  const { mutate: joinBubble, isLoading: isJoining } = useMutation(
    () => API.bubbles.join(bubbleId),
    {
      onSuccess: () => {
        qc.invalidateQueries(["bubble-public", bubbleId]);
        qc.invalidateQueries(["bubbles-list"]);
        setSuccessDialogOpen(true);
      },
      onError: (error) => {
        const errorMsg = error?.response?.data?.error || "Failed to join bubble";
        alert(errorMsg);
      },
    }
  );

  // ✅ NEW: Casting call click handler
  const handleCastingCallClick = useCallback((activity) => {
    if (activity.casting_call) {
      setSelectedCastingCall(activity.casting_call);
      setCastingCallPosterInfo({
        pfphref: activity.pfphref,
        display_name: activity.display_name,
      });
      setCastingCallViewDialogOpen(true);
    }
  }, []);

  // ✅ NEW: Casting call share menu handler
  const handleCastingCallShareClick = useCallback((e, activity) => {
    e.stopPropagation();
    setShareMenuActivity(activity);
    setShareMenuAnchor(e.currentTarget);
  }, []);

  // ✅ NEW: Share menu close handler
  const handleShareMenuClose = () => {
    setShareMenuAnchor(null);
    setShareMenuActivity(null);
  };

  // ✅ NEW: Copy public link handler
  const handleCopyPublicLink = async () => {
    if (shareMenuActivity?.casting_call?.public_slug) {
      const publicUrl = `${window.location.origin}/casting-calls/public/${shareMenuActivity.casting_call.public_slug}`;
      try {
        await navigator.clipboard.writeText(publicUrl);
        setSnack({ msg: 'Link copied to clipboard!', sev: 'success' });
      } catch (err) {
        setSnack({ msg: 'Failed to copy link', sev: 'error' });
      }
    }
    handleShareMenuClose();
  };

  // ✅ NEW: Open in new tab handler
  const handleOpenInNewTab = () => {
    if (shareMenuActivity?.casting_call?.public_slug) {
      window.open(`/casting-calls/public/${shareMenuActivity.casting_call.public_slug}`, '_blank');
    }
    handleShareMenuClose();
  };

  const handleJoin = () => {
    if (!isAuthenticated) {
      setLoginDialogOpen(true);
      return;
    }
    joinBubble();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (type) => {
    if (type?.includes('image')) return <ImageIcon sx={{ fontSize: 48 }} />;
    if (type?.includes('video')) return <VideoIcon sx={{ fontSize: 48 }} />;
    if (type === 'folder') return <FolderIcon sx={{ fontSize: 48 }} />;
    return <FileIcon sx={{ fontSize: 48 }} />;
  };

  if (isLoading) {
    return (
      <>
        <HomeHeader />
        <Box
          sx={{
            width: "100vw",
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress sx={{ color: "white", mb: 2 }} size={60} />
            <Typography variant="h6" sx={{ color: "white" }}>
              Loading bubble...
            </Typography>
          </Box>
        </Box>
      </>
    );
  }

  if (!bubble) {
    return (
      <>
        <HomeHeader />
        <Box
          sx={{
            width: "100vw",
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pt: 10,
          }}
        >
          <Typography variant="h6" color="text.secondary">
            Bubble not found.
          </Typography>
        </Box>
      </>
    );
  }

  const hasCoverImage = Boolean(bubble.cover_image);
  const recentPosts = feedData?.posts || [];
  const communityAssets = assetsData?.assets || [];
  const hasMoreAssets = assetsData?.has_more || false;
  const requiresMembership = assetsData?.requires_membership || false;

  return (
    <>
      <HomeHeader />
      <PageTitle title={`${bubble.name} - Blitz Bubble`} />

      <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
        {/* Hero Section */}
        <ModernGradientHeader 
          themeColors={bubble.theme_colors}
          hasCoverImage={hasCoverImage}
        >
          {hasCoverImage && (
            <CoverImageBackground>
              <img src={bubble.cover_image} alt="Bubble Cover" />
            </CoverImageBackground>
          )}

          <Container maxWidth="xl">
            <Box sx={{ py: { xs: 5, md: 7 }, position: "relative", zIndex: 3 }}>
              <Grid container spacing={4} alignItems="flex-end">
                {/* Left: Bubble Info */}
                <Grid item xs={12} lg={8}>
                  <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                    {/* Avatar */}
                    {bubble.avatar_image && (
                      <Avatar
                        src={bubble.avatar_image}
                        sx={{
                          width: 100,
                          height: 100,
                          border: "4px solid rgba(255,255,255,0.3)",
                          boxShadow: theme.shadows[8],
                        }}
                      />
                    )}

                    {/* Title and Badges */}
                    <Box flex={1}>
                      <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: "wrap" }}>
                        {bubble.is_public ? (
                          <StatusChip 
                            label="Public" 
                            icon={<PublicIcon />}
                            color="success"
                          />
                        ) : (
                          <StatusChip 
                            label="Private" 
                            icon={<LockIcon />}
                            color="default"
                          />
                        )}
                        
                        {bubble.is_paid && (
                          <StatusChip 
                            label={bubble.is_subscription 
                              ? `$${bubble.access_price}/${bubble.subscription_interval}`
                              : `$${bubble.access_price}`}
                            icon={<MoneyIcon />}
                            color="primary"
                          />
                        )}

                        {bubble.has_chat && (
                          <StatusChip 
                            label="Chat Enabled" 
                            icon={<ChatIcon />}
                            color="info"
                            variant="outlined"
                          />
                        )}
                      </Stack>

                      <Typography
                        variant="h1"
                        component="h1"
                        sx={{
                          fontWeight: 800,
                          letterSpacing: "-0.02em",
                          color: "common.white",
                          fontSize: { xs: "2rem", sm: "2.75rem", md: "3.5rem" },
                          lineHeight: 1.1,
                          wordBreak: "break-word",
                          textShadow: "0 4px 12px rgba(0,0,0,0.5)",
                        }}
                      >
                        {bubble.name}
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Description */}
                  {bubble.description && (
                    <Typography
                      variant="h6"
                      sx={{
                        opacity: 0.95,
                        fontWeight: 400,
                        color: "common.white",
                        lineHeight: 1.7,
                        fontSize: { xs: "1.1rem", md: "1.35rem" },
                        textShadow: "0 2px 8px rgba(0,0,0,0.5)",
                        maxWidth: "800px",
                      }}
                    >
                      {bubble.description}
                    </Typography>
                  )}
                </Grid>

                {/* Right: Host Info */}
                {bubble.host && (
                  <Grid item xs={12} lg={4}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2.5,
                        bgcolor: "rgba(255,255,255,0.2)",
                        backdropFilter: "blur(20px)",
                        borderRadius: 4,
                        p: 3,
                        border: "1px solid rgba(255,255,255,0.3)",
                        boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
                      }}
                    >
                      <Avatar
                        src={bubble.host.avatar || bubble.host.pfphref}
                        alt={bubble.host.display_name || bubble.host.username}
                        sx={{
                          width: 72,
                          height: 72,
                          border: "3px solid rgba(255,255,255,0.5)",
                          boxShadow: theme.shadows[6],
                        }}
                      />
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "rgba(255,255,255,0.85)",
                            fontWeight: 700,
                            mb: 0.5,
                            textTransform: "uppercase",
                            fontSize: "0.7rem",
                            letterSpacing: "1px",
                            display: "block",
                          }}
                        >
                          Hosted By
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            color: "common.white",
                            fontWeight: 700,
                            fontSize: "1.25rem",
                            textShadow: "0 2px 4px rgba(0,0,0,0.5)",
                          }}
                        >
                          {bubble.host.display_name || bubble.host.username}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Container>
        </ModernGradientHeader>

        {/* Content Section */}
        <Box sx={{ pb: 8 }}>
          <Container maxWidth="xl" sx={{ mt: -4, position: "relative", zIndex: 2 }}>
            <Grid container spacing={4}>
              {/* Left Column - Content */}
              <Grid item xs={12} md={8}>
                <Stack spacing={3}>
                  {/* Stats Grid */}
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <InfoCard elevation={3}>
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 3,
                            bgcolor: "primary.main",
                            color: "primary.contrastText",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <PeopleIcon sx={{ fontSize: 32 }} />
                        </Box>
                        <Box>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: "text.secondary",
                              fontWeight: 600,
                              mb: 0.5 
                            }}
                          >
                            Members
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            {bubble.member_count?.toLocaleString() || 0}
                          </Typography>
                        </Box>
                      </InfoCard>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <InfoCard elevation={3}>
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 3,
                            bgcolor: "success.main",
                            color: "success.contrastText",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <DescriptionIcon sx={{ fontSize: 32 }} />
                        </Box>
                        <Box>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: "text.secondary",
                              fontWeight: 600,
                              mb: 0.5 
                            }}
                          >
                            Total Posts
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            {bubble.total_posts?.toLocaleString() || 0}
                          </Typography>
                        </Box>
                      </InfoCard>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <InfoCard elevation={3}>
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 3,
                            bgcolor: "info.main",
                            color: "info.contrastText",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <TrendingIcon sx={{ fontSize: 32 }} />
                        </Box>
                        <Box>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: "text.secondary",
                              fontWeight: 600,
                              mb: 0.5 
                            }}
                          >
                            This Week
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            {bubble.recent_activity || 0}
                          </Typography>
                        </Box>
                      </InfoCard>
                    </Grid>
                  </Grid>

                  {/* About Card */}
                  <Card
                    sx={{
                      borderRadius: 4,
                      boxShadow: theme.shadows[3],
                      overflow: "hidden",
                    }}
                  >
                    <CardContent sx={{ p: 4 }}>
                      <Typography
                        variant="h6"
                        sx={{ 
                          mb: 2.5, 
                          fontWeight: 700, 
                          color: "text.primary",
                        }}
                      >
                        About This Community
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ 
                          lineHeight: 1.8, 
                          whiteSpace: "pre-wrap",
                          color: "text.secondary",
                          fontSize: "1rem",
                        }}
                      >
                        {bubble.description || "No description available"}
                      </Typography>

                      <Divider sx={{ my: 3 }} />

                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ 
                            mb: 1.5, 
                            fontWeight: 700, 
                            color: "text.secondary",
                            fontSize: "0.875rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px"
                          }}
                        >
                          Community Details
                        </Typography>
                        <Stack spacing={1.5}>
                          {bubble.created_at && (
                            <Box display="flex" alignItems="center" gap={1.5}>
                              <ScheduleIcon fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary">
                                Created {formatDate(bubble.created_at)}
                              </Typography>
                            </Box>
                          )}
                          <Box display="flex" alignItems="center" gap={1.5}>
                            {bubble.is_public ? (
                              <>
                                <PublicIcon fontSize="small" color="success" />
                                <Typography variant="body2" color="text.secondary">
                                  Anyone can join this community
                                </Typography>
                              </>
                            ) : (
                              <>
                                <LockIcon fontSize="small" color="action" />
                                <Typography variant="body2" color="text.secondary">
                                  Invitation required to join
                                </Typography>
                              </>
                            )}
                          </Box>
                        </Stack>
                      </Box>
                    </CardContent>
                  </Card>

                  {/* ✅ UPDATED: Recent Posts - Distinct rendering for posts vs casting calls */}
                  {recentPosts.length > 0 && (
                    <Card
                      sx={{
                        borderRadius: 4,
                        boxShadow: theme.shadows[3],
                        overflow: "hidden",
                      }}
                    >
                      <CardContent sx={{ p: 4 }}>
                        <Typography
                          variant="h6"
                          sx={{ 
                            mb: 3, 
                            fontWeight: 700, 
                            color: "text.primary",
                          }}
                        >
                          Recent Activity
                        </Typography>

                        <Stack spacing={2}>
                          {recentPosts.map((post) => {
                            // ✅ Render casting calls differently
                            if (post.activity_type === 'casting_call' || post.activity_type === 'CastingCall') {
                              const castingCall = post.casting_call;
                              if (!castingCall) return null;

                              return (
                                <CastingCallCard 
                                  key={post.id}
                                  elevation={2}
                                  onClick={() => {
                                    if (castingCall.public_slug) {
                                      navigate(`/casting-calls/public/${castingCall.public_slug}`);
                                    }
                                  }}
                                >
                                  {/* Header Image/Gradient */}
                                  <CastingCallHeader headerImage={castingCall.header_image}>
                                    <Box sx={{ position: 'relative', zIndex: 2, textAlign: 'center', px: 3 }}>
                                      <Chip
                                        label="Casting Call"
                                        icon={<CampaignIcon />}
                                        sx={{
                                          bgcolor: 'rgba(255,255,255,0.95)',
                                          fontWeight: 700,
                                          fontSize: '0.75rem',
                                          mb: 1,
                                          boxShadow: theme.shadows[4],
                                        }}
                                      />
                                      <Typography 
                                        variant="h6" 
                                        sx={{ 
                                          color: 'white',
                                          fontWeight: 700,
                                          textShadow: '0 2px 8px rgba(0,0,0,0.5)',
                                          lineHeight: 1.3,
                                        }}
                                      >
                                        {castingCall.brief}
                                      </Typography>
                                    </Box>
                                  </CastingCallHeader>

                                  {/* Content */}
                                  <Box sx={{ p: 2.5 }}>
                                    {/* Author */}
                                    <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                                      <Avatar 
                                        src={post.pfphref}
                                        sx={{ width: 40, height: 40 }}
                                      />
                                      <Box flex={1}>
                                        <Typography variant="subtitle2" fontWeight={600}>
                                          {post.display_name || 'Unknown'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {new Date(post.created_at).toLocaleDateString('en-US', { 
                                            month: 'short', 
                                            day: 'numeric',
                                            hour: 'numeric',
                                            minute: '2-digit'
                                          })}
                                        </Typography>
                                      </Box>
                                      <IconButton 
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setShareMenuActivity(post);
                                          setShareMenuAnchor(e.currentTarget);
                                        }}
                                      >
                                        <ShareIcon fontSize="small" />
                                      </IconButton>
                                    </Box>

                                    {/* Description */}
                                    {castingCall.description && (
                                      <Typography 
                                        variant="body2" 
                                        color="text.secondary"
                                        sx={{ 
                                          mb: 2,
                                          display: '-webkit-box',
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: 'vertical',
                                          overflow: 'hidden',
                                        }}
                                      >
                                        {castingCall.description}
                                      </Typography>
                                    )}

                                    {/* Details Grid */}
                                    <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                                      {castingCall.call_type && (
                                        <Chip 
                                          size="small" 
                                          label={castingCall.call_type}
                                          sx={{ 
                                            bgcolor: alpha(theme.palette.info.main, 0.1),
                                            color: 'info.main',
                                            fontWeight: 600,
                                          }}
                                        />
                                      )}
                                      {castingCall.budget && (
                                        <Chip 
                                          size="small" 
                                          icon={<MoneyIcon />}
                                          label={`$${castingCall.budget.toLocaleString()}`}
                                          sx={{ 
                                            bgcolor: alpha(theme.palette.success.main, 0.1),
                                            color: 'success.main',
                                            fontWeight: 600,
                                          }}
                                        />
                                      )}
                                      {castingCall.platforms?.length > 0 && (
                                        <Chip 
                                          size="small" 
                                          label={`${castingCall.platforms.length} platform${castingCall.platforms.length > 1 ? 's' : ''}`}
                                          variant="outlined"
                                        />
                                      )}
                                      {castingCall.work_types?.length > 0 && (
                                        <Chip 
                                          size="small" 
                                          label={castingCall.work_types[0]}
                                          variant="outlined"
                                        />
                                      )}
                                    </Box>

                                    {/* View Details Button */}
                                    <Button
                                      fullWidth
                                      variant="outlined"
                                      endIcon={<ArrowIcon />}
                                      sx={{
                                        borderColor: 'warning.main',
                                        color: 'warning.main',
                                        fontWeight: 600,
                                        '&:hover': {
                                          borderColor: 'warning.dark',
                                          bgcolor: alpha(theme.palette.warning.main, 0.08),
                                        }
                                      }}
                                    >
                                      View Casting Call Details
                                    </Button>
                                  </Box>
                                </CastingCallCard>
                              );
                            }

                            // ✅ Render regular posts with FeedItem
                            return (
                              <RegularPostCard key={post.id} sx={{ overflow: 'visible' }}>
                                <FeedItem
                                  activity={post}
                                  userAuth={{ userOK, creatorOK }}
                                  queryClient={qc}
                                  onSnackbarMessage={(msg, severity) => setSnack({ msg, sev: severity })}
                                  isCreator={isCreator}
                                  onCastingCallClick={handleCastingCallClick}
                                  onCastingCallShareClick={handleCastingCallShareClick}
                                />
                              </RegularPostCard>
                            );
                          })}
                        </Stack>

                        {bubble.is_member && (
                          <Box sx={{ mt: 3, textAlign: 'center' }}>
                            <Button
                              variant="text"
                              onClick={() => navigate(routes.bubblePublic.replace(':bubbleId', bubbleId))}
                              sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                              View All Posts
                            </Button>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </Stack>
              </Grid>

              {/* Right Column - Action Cards */}
              <Grid item xs={12} md={4}>
                <Stack spacing={3}>
                  {/* Join/Member Card */}
                  <Card
                    sx={{
                      borderRadius: 4,
                      boxShadow: theme.shadows[6],
                      position: { md: "sticky" },
                      top: { md: 100 },
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <CardContent sx={{ p: 4 }}>
                      {bubble.is_member ? (
                        <Box sx={{ textAlign: "center" }}>
                          <Box
                            sx={{
                              width: 80,
                              height: 80,
                              borderRadius: "50%",
                              bgcolor: "success.light",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mx: "auto",
                              mb: 3,
                            }}
                          >
                            <CheckCircleIcon
                              sx={{
                                fontSize: 48,
                                color: "success.main",
                              }}
                            />
                          </Box>
                          <Typography 
                            variant="h5" 
                            sx={{ 
                              mb: 2, 
                              fontWeight: 700,
                              color: "text.primary",
                            }}
                          >
                            You're a Member!
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ color: "text.secondary", lineHeight: 1.7, mb: 3 }}
                          >
                            Start engaging with the community by posting, commenting, and connecting with other members.
                          </Typography>
                          
                          <ModernButton
                            variant="contained"
                            fullWidth
                            size="large"
                            onClick={() => navigate(routes.bubblePublic.replace(':bubbleId', bubbleId))}
                            sx={{ mb: 2 }}
                          >
                            Go to Community Feed
                          </ModernButton>
                          
                          {bubble.has_chat && bubble.conversation_id && (
                            <ModernButton
                              variant="outlined"
                              fullWidth
                              size="large"
                              startIcon={<ChatIcon />}
                              onClick={() => navigate(`${routes.conversationshub}?conversation=${bubble.conversation_id}`)}
                              sx={{ 
                                borderColor: 'info.main',
                                color: 'info.main',
                                '&:hover': {
                                  borderColor: 'info.dark',
                                  bgcolor: alpha(theme.palette.info.main, 0.08)
                                }
                              }}
                            >
                              Open Bubble Chat
                            </ModernButton>
                          )}
                        </Box>
                      ) : (
                        <>
                          <Typography 
                            variant="h5" 
                            sx={{ 
                              mb: 2, 
                              fontWeight: 700,
                              textAlign: "center",
                            }}
                          >
                            Join This Community
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ 
                              color: "text.secondary", 
                              mb: 4,
                              textAlign: "center",
                              lineHeight: 1.7,
                            }}
                          >
                            Become a member to access posts, participate in discussions, and connect with others.
                          </Typography>
                          
                          {bubble.is_paid && (
                            <Box sx={{ mb: 3, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), borderRadius: 2 }}>
                              <Typography variant="h4" sx={{ fontWeight: 700, textAlign: 'center', mb: 1 }}>
                                ${bubble.access_price}
                                {bubble.is_subscription && (
                                  <Typography component="span" variant="body1" color="text.secondary">
                                    /{bubble.subscription_interval}
                                  </Typography>
                                )}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
                                {bubble.is_subscription ? 'Recurring payment' : 'One-time payment'}
                              </Typography>
                            </Box>
                          )}
                          
                          <ModernButton
                            variant="contained"
                            fullWidth
                            size="large"
                            onClick={handleJoin}
                            disabled={isJoining}
                            sx={{ 
                              mb: 2,
                              height: 56,
                              fontSize: "1.1rem",
                            }}
                          >
                            {isJoining ? (
                              <CircularProgress size={28} color="inherit" />
                            ) : bubble.is_paid ? (
                              "Join & Pay"
                            ) : (
                              "Join Community"
                            )}
                          </ModernButton>

                          <Stack direction="row" spacing={1} justifyContent="center">
                            <IconButton 
                              size="small" 
                              sx={{ color: 'text.secondary' }}
                              onClick={() => {
                                if (navigator.share) {
                                  navigator.share({
                                    title: bubble.name,
                                    text: bubble.description,
                                    url: window.location.href
                                  });
                                }
                              }}
                            >
                              <ShareIcon />
                            </IconButton>
                          </Stack>
                        </>
                      )}
                    </CardContent>
                  </Card>

                  {/* Community Assets Card */}
                  <Card
                    sx={{
                      borderRadius: 4,
                      boxShadow: theme.shadows[3],
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2.5}>
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <FolderIcon color="primary" />
                          <Typography
                            variant="h6"
                            sx={{ 
                              fontWeight: 700, 
                              color: "text.primary",
                            }}
                          >
                            Community Assets
                          </Typography>
                        </Box>
                        {!assetsLoading && (
                          <Badge 
                            badgeContent={assetsData?.total_count || 0} 
                            color="primary"
                            sx={{
                              '& .MuiBadge-badge': {
                                fontWeight: 700,
                              }
                            }}
                          />
                        )}
                      </Box>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 3, lineHeight: 1.6 }}
                      >
                        Shared resources, files, and materials for community members.
                      </Typography>

                      {assetsLoading ? (
                        <Stack spacing={2}>
                          {[1, 2, 3].map((i) => (
                            <Box key={i}>
                              <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 2, mb: 1 }} />
                              <Skeleton variant="text" width="60%" />
                              <Skeleton variant="text" width="40%" />
                            </Box>
                          ))}
                        </Stack>
                      ) : (
                        <>
                          <Stack spacing={2}>
                            {communityAssets.map((asset, index) => (
                              <AssetCard 
                                key={asset.id || index}
                                elevation={1}
                                onClick={() => {
                                  if (!bubble.is_member && !asset.is_real) {
                                    setLoginDialogOpen(true);
                                  }
                                }}
                              >
                                {asset.type === 'folder' ? (
                                  <Box sx={{ p: 2 }}>
                                    <Box display="flex" alignItems="center" gap={2}>
                                      <Box
                                        sx={{
                                          width: 56,
                                          height: 56,
                                          borderRadius: 2,
                                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        <FolderIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                                      </Box>
                                      <Box flex={1}>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                                          {asset.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {asset.file_count} files
                                        </Typography>
                                      </Box>
                                      {!asset.is_real && (
                                        <Chip 
                                          label="Preview" 
                                          size="small" 
                                          variant="outlined"
                                          sx={{ fontSize: '0.7rem' }}
                                        />
                                      )}
                                    </Box>
                                  </Box>
                                ) : (
                                  <>
                                    {asset.preview_url && (
                                      <AssetPreview>
                                        <img src={asset.preview_url} alt={asset.name} />
                                        {asset.size && (
                                          <Box
                                            sx={{
                                              position: 'absolute',
                                              top: 8,
                                              right: 8,
                                              bgcolor: 'rgba(0,0,0,0.6)',
                                              borderRadius: 1,
                                              px: 1,
                                              py: 0.5,
                                            }}
                                          >
                                            <Typography variant="caption" sx={{ color: 'white', fontWeight: 600 }}>
                                              {formatFileSize(asset.size)}
                                            </Typography>
                                          </Box>
                                        )}
                                        {!asset.is_real && (
                                          <Box
                                            sx={{
                                              position: 'absolute',
                                              top: 8,
                                              left: 8,
                                              bgcolor: 'rgba(255,255,255,0.9)',
                                              borderRadius: 1,
                                              px: 1,
                                              py: 0.5,
                                            }}
                                          >
                                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                              Preview
                                            </Typography>
                                          </Box>
                                        )}
                                      </AssetPreview>
                                    )}
                                    <Box sx={{ p: 2 }}>
                                      <Box display="flex" alignItems="flex-start" gap={1.5}>
                                        {!asset.preview_url && (
                                          <Box
                                            sx={{
                                              width: 48,
                                              height: 48,
                                              borderRadius: 2,
                                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              flexShrink: 0,
                                            }}
                                          >
                                            {getFileIcon(asset.type)}
                                          </Box>
                                        )}
                                        <Box flex={1}>
                                          <Box display="flex" alignItems="center" gap={1}>
                                            <Typography 
                                              variant="subtitle2" 
                                              fontWeight={700} 
                                              sx={{ 
                                                mb: 0.5,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                flex: 1
                                              }}
                                            >
                                              {asset.name}
                                            </Typography>
                                            {!asset.is_real && (
                                              <Chip 
                                                label="Preview" 
                                                size="small" 
                                                variant="outlined"
                                                sx={{ fontSize: '0.65rem', height: 20 }}
                                              />
                                            )}
                                          </Box>
                                          <Stack direction="row" spacing={1} alignItems="center">
                                            {!asset.preview_url && asset.size && (
                                              <Typography variant="caption" color="text.secondary">
                                                {formatFileSize(asset.size)}
                                              </Typography>
                                            )}
                                            {asset.organizer_name && (
                                              <>
                                                {!asset.preview_url && asset.size && (
                                                  <Typography variant="caption" color="text.secondary">•</Typography>
                                                )}
                                                <Typography variant="caption" color="text.secondary">
                                                  {asset.organizer_name}
                                                </Typography>
                                              </>
                                            )}
                                          </Stack>
                                        </Box>
                                      </Box>
                                    </Box>
                                  </>
                                )}
                              </AssetCard>
                            ))}
                          </Stack>

                          {requiresMembership && (
                            <Box 
                              sx={{ 
                                mt: 3, 
                                p: 2, 
                                borderRadius: 2,
                                bgcolor: alpha(theme.palette.info.main, 0.08),
                                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                                textAlign: 'center'
                              }}
                            >
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                <LockIcon sx={{ fontSize: 16 }} />
                                Join to access all community assets
                              </Typography>
                            </Box>
                          )}

                          {bubble.is_member && hasMoreAssets && (
                            <Box sx={{ mt: 3, textAlign: 'center' }}>
                              <Button
                                variant="text"
                                startIcon={<VisibilityIcon />}
                                onClick={() => navigate(`/bubbles/${bubbleId}/assets`)}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                              >
                                View All Assets
                              </Button>
                            </Box>
                          )}
                        </>
                      )}
                    </CardContent>
                  </Card>
                </Stack>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>

      {/* Login Dialog */}
      <Dialog
        open={loginDialogOpen}
        onClose={() => setLoginDialogOpen(false)}
        PaperProps={{
          sx: { borderRadius: 4, p: 2, maxWidth: 480 },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", pt: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Please Login or Create an Account
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 3 }}>
          <Typography align="center" sx={{ color: "text.secondary", lineHeight: 1.7 }}>
            You must be logged in to join bubbles and access community assets. Create an account or login to get started.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ gap: 2, px: 3, pb: 3, justifyContent: "center" }}>
          <Button 
            onClick={() => navigate(routes.login)} 
            variant="outlined"
            size="large"
            sx={{ 
              borderRadius: 3,
              px: 4,
              fontWeight: 600,
            }}
          >
            Login
          </Button>
          <ModernButton
            onClick={() => navigate(bubble.host_global_user_id ? `/invite/${bubble.host_global_user_id}` : routes.onboard)}
            variant="contained"
            size="large"
          >
            Create Account
          </ModernButton>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <Dialog
        open={successDialogOpen}
        onClose={() => setSuccessDialogOpen(false)}
        PaperProps={{
          sx: { borderRadius: 4, p: 3, maxWidth: 480 },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", pt: 2 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              bgcolor: "success.light",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <CheckCircleIcon
              sx={{ fontSize: 48, color: "success.main" }}
            />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Welcome to {bubble.name}!
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography align="center" sx={{ color: "text.secondary", lineHeight: 1.7 }}>
            You've successfully joined the community. Start exploring posts, connecting with members, and accessing shared resources.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
          <ModernButton
            onClick={() => {
              setSuccessDialogOpen(false);
              navigate(routes.bubblePublic.replace(':bubbleId', bubbleId));
            }}
            variant="contained"
            color="success"
            size="large"
          >
            Start Exploring
          </ModernButton>
        </DialogActions>
      </Dialog>

      {/* ✅ NEW: Casting Call View Dialog */}
      <CastingCallDialog
        open={castingCallViewDialogOpen}
        onClose={() => {
          setCastingCallViewDialogOpen(false);
          setSelectedCastingCall(null);
          setCastingCallPosterInfo(null);
        }}
        castingCall={selectedCastingCall}
        posterInfo={castingCallPosterInfo}
      />

      {/* ✅ NEW: Casting Call Share Menu */}
      <Menu
        anchorEl={shareMenuAnchor}
        open={Boolean(shareMenuAnchor)}
        onClose={handleShareMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleCopyPublicLink}>
          <ListItemIcon>
            <LinkIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy public link</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleOpenInNewTab}>
          <ListItemIcon>
            <OpenInNewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Open in new tab</ListItemText>
        </MenuItem>
      </Menu>

      {/* Snackbar for notifications */}
      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snack && (
          <Alert severity={snack.sev} variant="filled" onClose={() => setSnack(null)}>
            {snack.msg}
          </Alert>
        )}
      </Snackbar>
    </>
  );
}